/**
 * Pure decision logic for the boot-splash watchdog's FIRST checkpoint
 * (`AUTO_RECOVER_AFTER_MS`), extracted so the loop-safety contract is
 * unit-testable without timers, DOM, navigation, or Sentry. Mirrors
 * `providers/AuthProvider/resolveAuthAction.ts` — the same "encode the guard
 * contract in a tested pure function" pattern that hardened the CUSTOMER-UI-3T
 * auth-redirect loop.
 *
 * Automatic recovery (a clean reload) is intentionally NARROW: it fires only
 * when there is positive evidence a reload will actually help AND it cannot be
 * a healthy-but-slow boot. The signal is an OAuth `?error` in the URL — a
 * Keycloak callback that definitively FAILED. A clean reload drops those stale
 * params so auth can restart; and a healthy boot (authenticated user waiting on
 * a slow /self, or mid-redirect) never carries `?error`, so it is never
 * interrupted. Every other stuck splash (clean URL — could be slow-but-healthy,
 * could be wedged) falls through to the LATER checkpoint, which surfaces the
 * manual "Reload app" button (and alerts) without ever auto-reloading.
 */
export type SplashAction =
  // Do nothing now: splash already cleared, a redirect is in flight (healthy
  // slow round-trip), no recoverable signal, or the recovery budget is spent.
  // The later checkpoint surfaces the manual button if the splash is still up.
  | "wait"
  // A failed-callback wedge with recovery budget available — attempt one clean
  // reload to drop the stale `?error` params.
  | "auto-recover";

export interface SplashSignals {
  /** Is the boot-splash overlay still actually visible? */
  loaderVisible: boolean;
  /**
   * Is an automatic Keycloak signinRedirect in flight? Such a splash is the
   * slow-but-healthy auth round-trip (CUSTOMER-UI-6B / CUSTOMER-UI-3Z) that
   * self-heals when the callback lands — never auto-reload it (that would abort
   * the round-trip).
   */
  redirectInFlight: boolean;
  /**
   * Does the URL carry an OAuth `?error` — a Keycloak callback that failed? This
   * is the ONLY state we auto-recover: a clean reload drops the bad params, and
   * it cannot be confused with a healthy slow boot (which never has `?error`).
   */
  hasRecoverableError: boolean;
  /**
   * May an automatic recovery be attempted now? False once the one-shot
   * recovery budget is spent (we already auto-reloaded this window) or when
   * auto-recovery is disabled (Storybook/tests).
   */
  autoRecoverEligible: boolean;
}

export function resolveSplashAction({
  loaderVisible,
  redirectInFlight,
  hasRecoverableError,
  autoRecoverEligible,
}: SplashSignals): SplashAction {
  if (!loaderVisible) return "wait";
  // Healthy slow auth round-trip — never interrupt it.
  if (redirectInFlight) return "wait";
  // Only auto-reload a definitively-failed callback we still have budget for.
  // Everything else (clean URL: slow-but-healthy OR wedged) waits for the
  // manual-button checkpoint rather than risking an interrupted/futile reload.
  if (hasRecoverableError && autoRecoverEligible) return "auto-recover";
  return "wait";
}
